// Hook for calling the run-agent-skill edge function with SSE streaming.
//
// Uses direct fetch (NOT invokeEdgeFunction) per editor spec §5.3:
// the shared invokeEdgeFunction wrapper assumes JSON request/response with
// retries; SSE streaming is incompatible with that pattern.

import { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { RunAgentSkillRequest, RunAgentSkillUsage } from "@/types/edge-functions";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

export interface RunAgentSkillState {
  isStreaming: boolean;
  output: string;
  usage: RunAgentSkillUsage | null;
  latencyMs: number | null;
  quotaRemaining: number | null;
  error: { code: string; message: string } | null;
}

export interface UseRunAgentSkillReturn extends RunAgentSkillState {
  run: (req: RunAgentSkillRequest) => Promise<void>;
  abort: () => void;
}

/**
 * Parse SSE frames from a ReadableStream.
 * Frames are: "event: <name>\ndata: <JSON>\n\n"
 */
async function* parseSseFrames(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  signal: AbortSignal
): AsyncGenerator<{ event: string; data: unknown }> {
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      if (signal.aborted) break;

      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Split on double-newline (SSE frame boundary)
      const frames = buffer.split("\n\n");
      // Keep last (potentially incomplete) chunk in buffer
      buffer = frames.pop() ?? "";

      for (const frame of frames) {
        if (frame.trim() === "") continue;

        let eventName = "message";
        let dataLine = "";

        for (const line of frame.split("\n")) {
          if (line.startsWith("event: ")) {
            eventName = line.slice("event: ".length).trim();
          } else if (line.startsWith("data: ")) {
            dataLine = line.slice("data: ".length).trim();
          }
        }

        if (dataLine) {
          try {
            const parsed = JSON.parse(dataLine) as unknown;
            yield { event: eventName, data: parsed };
          } catch {
            // Ignore unparseable frames
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export function useRunAgentSkill(): UseRunAgentSkillReturn {
  const [state, setState] = useState<RunAgentSkillState>({
    isStreaming: false,
    output: "",
    usage: null,
    latencyMs: null,
    quotaRemaining: null,
    error: null,
  });

  const controllerRef = useRef<AbortController | null>(null);

  // Auto-abort on unmount
  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const abort = useCallback(() => {
    controllerRef.current?.abort();
  }, []);

  const run = useCallback(async (req: RunAgentSkillRequest) => {
    // Abort any in-flight request
    controllerRef.current?.abort();

    const controller = new AbortController();
    controllerRef.current = controller;

    setState({
      isStreaming: true,
      output: "",
      usage: null,
      latencyMs: null,
      quotaRemaining: null,
      error: null,
    });

    try {
      const session = await supabase.auth.getSession();
      const jwt = session.data.session?.access_token;

      if (!jwt) {
        setState((prev) => ({
          ...prev,
          isStreaming: false,
          error: { code: "auth_error", message: "Not authenticated." },
        }));
        return;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/run-agent-skill`, {
        method: "POST",
        signal: controller.signal,
        headers: {
          authorization: `Bearer ${jwt}`,
          "content-type": "application/json",
        },
        body: JSON.stringify(req),
      });

      if (!response.ok) {
        let errorCode = "request_failed";
        let errorMessage = `HTTP ${response.status}`;
        try {
          const json = (await response.json()) as { code?: string; message?: string };
          errorCode = json.code ?? errorCode;
          errorMessage = json.message ?? errorMessage;
        } catch {
          // ignore parse error
        }
        setState((prev) => ({
          ...prev,
          isStreaming: false,
          error: { code: errorCode, message: errorMessage },
        }));
        return;
      }

      if (!response.body) {
        setState((prev) => ({
          ...prev,
          isStreaming: false,
          error: { code: "no_body", message: "Response body is empty." },
        }));
        return;
      }

      const reader = response.body.getReader();

      for await (const { event, data } of parseSseFrames(reader, controller.signal)) {
        if (controller.signal.aborted) break;

        if (event === "chunk") {
          const chunk = data as { text: string };
          setState((prev) => ({ ...prev, output: prev.output + chunk.text }));
        } else if (event === "done") {
          const done = data as {
            usage: RunAgentSkillUsage;
            latency_ms: number;
            quota_remaining: number;
          };
          setState((prev) => ({
            ...prev,
            isStreaming: false,
            usage: done.usage,
            latencyMs: done.latency_ms,
            quotaRemaining: done.quota_remaining,
          }));
          return;
        } else if (event === "error") {
          const err = data as { code: string; message: string };
          setState((prev) => ({
            ...prev,
            isStreaming: false,
            error: err,
          }));
          return;
        }
      }

      // Stream ended without a done frame (e.g., aborted)
      setState((prev) => ({ ...prev, isStreaming: false }));
    } catch (err) {
      if ((err as { name?: string }).name === "AbortError") {
        setState((prev) => ({ ...prev, isStreaming: false }));
        return;
      }
      console.error("useRunAgentSkill", err);
      setState((prev) => ({
        ...prev,
        isStreaming: false,
        error: {
          code: "client_error",
          message: (err as Error).message ?? "An unexpected error occurred.",
        },
      }));
    }
  }, []);

  return { ...state, run, abort };
}
